import { cn } from "@/lib/utils";
import { cva, VariantProps } from "class-variance-authority";
import React from "react";

const backgroundVariants = cva("", {
  variants: {
    backgroundVariant: {
      default: "stroke-blue-200",
      transparent: "stroke-transparent",
      neutral: "stroke-gray-200",
      warning: "stroke-yellow-200",
      error: "stroke-red-200",
      success: "stroke-emerald-200",
    },
  },
  defaultVariants: {
    backgroundVariant: "default",
  },
});

const circleVariants = cva("", {
  variants: {
    circleVariant: {
      default: "stroke-secondary-foreground",
      neutral: "stroke-gray-500",
      warning: "stroke-yellow-500",
      error: "stroke-red-500",
      success: "stroke-emerald-500",
    },
  },
  defaultVariants: {
    circleVariant: "default",
  },
});

interface ProgressCircleProps
  extends Omit<React.SVGProps<SVGSVGElement>, "value">,
    VariantProps<typeof backgroundVariants>,
    VariantProps<typeof circleVariants> {
  value?: number;
  max?: number;
  showAnimation?: boolean;
  radius?: number;
  strokeWidth?: number;
  children?: React.ReactNode;
  foregroundColor?: string;
}

const ProgressCircle = React.forwardRef<SVGSVGElement, ProgressCircleProps>(
  (
    {
      value = 0,
      max = 100,
      radius = 32,
      strokeWidth = 6,
      showAnimation = true,
      backgroundVariant,
      circleVariant,
      className,
      children,
      foregroundColor,
      ...props
    }: ProgressCircleProps,
    forwardedRef,
  ) => {
    const safeValue = Math.min(max, Math.max(value, 0));
    const normalizedRadius = radius - strokeWidth / 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const offset = circumference - (safeValue / max) * circumference;

    const background = backgroundVariants({ backgroundVariant });
    const circle = circleVariants({ circleVariant });

    return (
      <div
        className="relative inline-flex items-center"
        role="progressbar"
        aria-label="Progress circle"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        data-max={max}
        data-value={safeValue ?? null}
      >
        <svg
          ref={forwardedRef}
          width={radius * 2}
          height={radius * 2}
          viewBox={`0 0 ${radius * 2} ${radius * 2}`}
          className={cn("-rotate-90 transform", className)}
          {...props}
        >
          <circle
            r={normalizedRadius}
            cx={radius}
            cy={radius}
            strokeWidth={strokeWidth}
            fill="transparent"
            stroke={""}
            strokeLinecap="round"
            className={cn("transition-colors ease-linear", background)}
          />
          {safeValue >= 0 ? (
            <circle
              r={normalizedRadius}
              cx={radius}
              cy={radius}
              strokeWidth={strokeWidth}
              strokeDasharray={`${circumference} ${circumference}`}
              strokeDashoffset={offset}
              fill="transparent"
              stroke={foregroundColor}
              strokeLinecap="round"
              className={cn(
                "bg-primary transition-colors ease-linear",
                !foregroundColor && circle,
                showAnimation &&
                  "transform-gpu transition-all duration-300 ease-in-out",
              )}
            />
          ) : null}
        </svg>
        <div
          className={cn("absolute inset-0 flex items-center justify-center")}
        >
          {children}
        </div>
      </div>
    );
  },
);

ProgressCircle.displayName = "ProgressCircle";

export { ProgressCircle, type ProgressCircleProps };
